import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Input from 'src/components/ui/Input/Input';
import ConfirmationModal from 'src/components/modals/ConfirmationModal/ConfirmationModal';
import { currentLocale, localeNumber } from 'src/localization/locale';
import { formatWeightPercent } from 'src/pages/Forecasts/weightFormat';
import {
  CustomerForecast,
  DEFAULT_CUTOFF_BEFORE_PCT,
  ForecastWeights,
  PRESET_WEIGHTS,
  WeightingPreset,
  useCancelForecast,
  useSaveForecastWizard,
  useSubmitForecast,
} from 'src/services/forecasts';

type Step = 1 | 2 | 3;

const WEEKDAY_KEYS: (keyof ForecastWeights)[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const PRESETS: WeightingPreset[] = ['realistic', 'equal'];

interface Props {
  forecast: CustomerForecast;
  onClose: () => void;
}

interface DailyRow {
  date: Date;
  weekdayIndex: number; // 0=Mon..6=Sun
  isWorkday: boolean;
  weight: number;
  imports: number;
  shipped: number;
}

function buildDailyDistribution(
  year: number,
  month: number,
  total: number,
  weights: ForecastWeights,
  cutoffBeforePct: number,
  initialPending: number = 0,
): DailyRow[] {
  // Mirrors the BE recompute_lines_from_weights:
  //   imports_d = total * weight_d / Σ weight_d   (Σ over all calendar days)
  //   shipped_d = pending + today_before          (workday only)
  //   pending += today_after on workdays, += today's imports on weekends
  // `initialPending` is the carry-in from the prior month's forecast
  // (after-cutoff of its last workday + trailing weekend imports). Final
  // pending at month end is carried OUT to the next month — no end-of-
  // month dump. Per-month sum of `shipped` therefore equals
  //     total + initialPending - finalPending
  // not `total` exactly; this is by design and matches the BE.
  const days = new Date(year, month, 0).getDate();
  const rows: DailyRow[] = [];
  let sumW = 0;
  for (let d = 1; d <= days; d++) {
    const dt = new Date(year, month - 1, d);
    const js = dt.getDay();
    const idx = (js + 6) % 7;
    const isWorkday = idx < 5;
    const w = weights[WEEKDAY_KEYS[idx]];
    rows.push({ date: dt, weekdayIndex: idx, isWorkday, weight: w, imports: 0, shipped: 0 });
    sumW += w;
  }
  if (sumW <= 0 || total <= 0) return rows;
  rows.forEach(r => {
    r.imports = (total * r.weight) / sumW;
  });

  const beforeShare = Math.max(0, Math.min(100, cutoffBeforePct)) / 100;
  let pending = Math.max(0, initialPending);
  let running = 0;
  let lastWorkdayIdx = -1;
  let finalPending = 0;
  rows.forEach((r, i) => {
    const todayBefore = r.imports * beforeShare;
    const todayAfter = r.imports - todayBefore;
    if (r.isWorkday) {
      const shipped = Math.max(0, Math.round(pending + todayBefore));
      r.shipped = shipped;
      if (shipped > 0) lastWorkdayIdx = i;
      running += shipped;
      pending = todayAfter;
    } else {
      pending += r.imports;
    }
  });
  finalPending = pending;
  const expected = Math.round(total + initialPending - finalPending);
  const residual = expected - running;
  if (residual !== 0 && lastWorkdayIdx >= 0) {
    rows[lastWorkdayIdx].shipped = Math.max(0, rows[lastWorkdayIdx].shipped + residual);
  }
  return rows;
}

interface WeeklyRow {
  key: keyof ForecastWeights;
  isWorkday: boolean;
  weight: number;
  imports: number;
  shipped: number | null; // null on weekend (accumulated to Mon)
}

function buildArchetypalWeek(avgPerDay: number, weights: ForecastWeights, cutoffBeforePct: number): WeeklyRow[] {
  // 7-day steady-state illustration used in the wizard table. Assumes a
  // continuous flow: prior week's Sat/Sun + Fri's after-cutoff land on the
  // current week's Monday. This is what the user reasons about when
  // comparing presets — not a particular calendar month.
  const beforeShare = Math.max(0, Math.min(100, cutoffBeforePct)) / 100;
  const imports = WEEKDAY_KEYS.map(k => avgPerDay * weights[k]);
  const before = imports.map(v => v * beforeShare);
  const after = imports.map((v, i) => v - before[i]);

  // Steady-state Monday absorbs prior Fri-after + Sat + Sun.
  const priorMonAbsorb = after[4] + imports[5] + imports[6];

  return WEEKDAY_KEYS.map((k, i) => {
    const isWorkday = i < 5;
    let shipped: number | null;
    if (!isWorkday) {
      shipped = null;
    } else if (i === 0) {
      shipped = before[0] + priorMonAbsorb;
    } else {
      shipped = before[i] + after[i - 1];
    }
    return { key: k, isWorkday, weight: weights[k], imports: imports[i], shipped };
  });
}

function StepDot({ n, label, active }: { n: number; label: string; active: boolean }) {
  return (
    <div className="flex items-center gap-8">
      <div
        className={`w-24 h-24 rounded-full flex items-center justify-center typo-text-12--bold ${
          active ? 'surface-active text-on-active' : 'surface-base text-faded'
        }`}>
        {n}
      </div>
      <span className={`typo-text-14 ${active ? 'text-full font-semibold' : 'text-faded'}`}>{label}</span>
    </div>
  );
}

function StepHeader({ step }: { step: Step }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-16 justify-center my-16">
      <StepDot n={1} label={t('forecastWizard.step.packets')} active={step === 1} />
      <div className="w-32 h-px surface-base" />
      <StepDot n={2} label={t('forecastWizard.step.weighting')} active={step === 2} />
      <div className="w-32 h-px surface-base" />
      <StepDot n={3} label={t('forecastWizard.step.confirmation')} active={step === 3} />
    </div>
  );
}

export default function ForecastWizard({ forecast, onClose }: Props) {
  const { t } = useTranslation();
  const saveWizard = useSaveForecastWizard();
  const submit = useSubmitForecast();
  const cancel = useCancelForecast();

  const [step, setStep] = useState<Step>(1);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [total, setTotal] = useState<number>(forecast.input_total_packets ?? 0);
  const [preset, setPreset] = useState<WeightingPreset>(forecast.weighting_preset ?? 'realistic');
  const [weights, setWeights] = useState<ForecastWeights>(forecast.weights ?? PRESET_WEIGHTS.realistic);
  const [error, setError] = useState<string | null>(null);

  const workingDays = forecast.workdays_in_month;
  const daysInMonth = forecast.days_in_month;
  const avgPerDay = daysInMonth > 0 ? total / daysInMonth : 0;

  const carryIn = forecast.carry_in_packets ?? 0;
  const distribution = useMemo(
    () => buildDailyDistribution(forecast.year, forecast.month, total, weights, DEFAULT_CUTOFF_BEFORE_PCT, carryIn),
    [forecast.year, forecast.month, total, weights, carryIn],
  );

  const weeklyArchetype = useMemo(
    () => buildArchetypalWeek(avgPerDay, weights, DEFAULT_CUTOFF_BEFORE_PCT),
    [avgPerDay, weights],
  );

  function applyPreset(p: WeightingPreset) {
    setPreset(p);
    if (p !== 'custom') setWeights(PRESET_WEIGHTS[p]);
  }

  function setWeight(key: keyof ForecastWeights, value: number) {
    setPreset('custom');
    setWeights(prev => ({ ...prev, [key]: value }));
  }

  async function persistDraft(): Promise<boolean> {
    setError(null);
    try {
      await saveWizard.mutateAsync({
        id: forecast.id,
        payload: {
          total_packets: total,
          weights,
          preset,
          // The before/after cut-off split is not editable in the wizard any
          // more: preview and payload always use the fixed default (30 / 70),
          // regardless of what an older draft may have stored.
          cutoff_before_pct: DEFAULT_CUTOFF_BEFORE_PCT,
        },
      });
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    }
  }

  async function onSaveDraft() {
    if (await persistDraft()) onClose();
  }

  async function onSubmit() {
    if (!(await persistDraft())) return;
    try {
      await submit.mutateAsync(forecast.id);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function onCancelConfirmed(confirmed: boolean) {
    setCancelOpen(false);
    if (!confirmed) return;
    setError(null);
    try {
      await cancel.mutateAsync(forecast.id);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const monthLabel = new Date(forecast.year, forecast.month - 1, 1).toLocaleDateString(currentLocale(), {
    year: 'numeric',
    month: 'long',
  });

  return (
    <section className="surface-elevated border border-subtle rounded-base card-shadow overflow-hidden">
      <div className="px-24 py-16 border-subtle-bottom flex items-center justify-between">
        <div className="flex items-center gap-8">
          <h2 className="typo-text-16--bold text-full">{monthLabel}</h2>
          <span className="typo-text-12 text-faded surface-base px-8 py-2 rounded-full">
            {t(`forecasts.state.${forecast.state}`)}
          </span>
        </div>
        <div className="flex items-center gap-12">
          {forecast.state === 'submitted' && (
            <Button
              tone={ButtonToneProps.DESTRUCTIVE}
              title={t('forecasts.storno')}
              onClick={() => setCancelOpen(true)}
              disabled={cancel.isPending}
            />
          )}
          <button type="button" onClick={onClose} className="typo-text-14 text-faded hover:text-full">
            {t('forecasts.close')}
          </button>
        </div>
      </div>
      <ConfirmationModal
        isOpen={cancelOpen}
        onClose={confirmed => onCancelConfirmed(confirmed)}
        title={t('forecasts.stornoConfirmTitle')}
        description={t('forecasts.stornoConfirmText')}
      />

      <StepHeader step={step} />

      {/* Step 1 — Packets */}
      {step === 1 && (
        <div className="px-24 py-24 flex flex-col gap-16">
          <div className="typo-text-14 text-faded">
            {monthLabel} — {t('forecastWizard.workingDays', { count: workingDays })}
          </div>
          <div className="flex flex-col gap-6">
            <span className="typo-text-12 text-faded font-semibold uppercase tracking-wider">
              ◎ {t('forecastWizard.totalPackets')}
            </span>
            <Input
              type="text"
              value={total === 0 ? '' : String(total)}
              placeholder={t('forecastWizard.totalPacketsPlaceholder')}
              onChange={e => {
                const cleaned = e.target.value.replace(/[^0-9]/g, '');
                setTotal(cleaned === '' ? 0 : Math.max(0, Number(cleaned)));
              }}
            />
          </div>
          <div className="surface-base rounded-base px-12 py-8 typo-text-12 text-faded">
            ≈ {workingDays > 0 ? Math.round(total / workingDays) : 0} {t('forecastWizard.packetsPerDay')}
          </div>
          {error && <div className="typo-text-12 status-error-full">{error}</div>}
          <div className="flex justify-end">
            <Button
              tone={ButtonToneProps.PRIMARY_BLUE}
              title={t('forecastWizard.next')}
              onClick={() => setStep(2)}
              disabled={total <= 0}
            />
          </div>
        </div>
      )}

      {/* Step 2 — Weighting */}
      {step === 2 && (
        <div className="px-24 py-24 flex flex-col gap-16">
          <p className="typo-text-12 text-faded">{t('forecastWizard.weightingHint')}</p>

          {/* Aufträge/Tag AVG header */}
          <div className="flex items-baseline justify-between surface-base rounded-base px-16 py-12">
            <span className="typo-text-12 text-faded font-semibold uppercase tracking-wider">
              {t('forecastWizard.avgOrdersPerDay')}
            </span>
            <span className="typo-text-16--bold text-full">{Math.round(avgPerDay)}</span>
          </div>

          {/* Preset Buttons */}
          <div className="flex flex-col gap-8">
            <div className="typo-text-12 text-faded font-semibold uppercase tracking-wider">
              {t('forecastWizard.weightingPresetTitle')}
            </div>
            <div className="flex flex-wrap gap-8">
              {PRESETS.map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => applyPreset(p)}
                  className={`px-12 py-6 rounded-base typo-text-14 border ${
                    preset === p
                      ? 'border-active text-full surface-elevated typo-text-14--bold'
                      : 'border-subtle text-faded surface-base'
                  }`}>
                  {t(`forecastWizard.preset.${p}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Weekly table: weight slider + Importe + Versendet, 7 days */}
          <div className="border border-subtle rounded-base overflow-hidden">
            <table className="w-full">
              <thead className="surface-base">
                <tr className="border-b border-subtle">
                  <th className="px-12 py-8 text-left typo-text-10 text-faded font-semibold uppercase tracking-wider">
                    {t('forecastWizard.dayColumn')}
                  </th>
                  <th className="px-12 py-8 text-left typo-text-10 text-faded font-semibold uppercase tracking-wider">
                    {t('forecastWizard.weightColumn')}
                  </th>
                  <th className="px-12 py-8 text-right typo-text-10 text-faded font-semibold uppercase tracking-wider">
                    {t('forecastWizard.importsColumn')}
                  </th>
                  <th className="px-12 py-8 text-right typo-text-10 text-faded font-semibold uppercase tracking-wider">
                    {t('forecastWizard.shippedColumn')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {weeklyArchetype.map(row => (
                  <tr key={row.key} className="border-b border-subtle">
                    <td className="px-12 py-6 typo-text-12 text-full w-48">{t(`forecastWizard.weekday.${row.key}`)}</td>
                    <td className="px-12 py-6">
                      <div className="flex items-center gap-8">
                        <input
                          type="range"
                          min={0}
                          max={2}
                          step={0.1}
                          value={weights[row.key]}
                          onChange={e => setWeight(row.key, Number(e.target.value))}
                          className="flex-1"
                        />
                        <span
                          className={`w-48 text-right typo-text-12--bold ${
                            row.isWorkday ? 'text-full' : 'status-warning-full'
                          }`}>
                          {formatWeightPercent(row.weight)}
                        </span>
                      </div>
                    </td>
                    <td className="px-12 py-6 text-right typo-text-14--bold text-full">{Math.round(row.imports)}</td>
                    <td className="px-12 py-6 text-right typo-text-12--bold">
                      {row.shipped === null ? <span className="text-faded">—</span> : Math.round(row.shipped)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-12 py-6 typo-text-12 text-faded surface-base">
              {t('forecastWizard.weekendShippedNote')}
            </div>
          </div>

          {error && <div className="typo-text-12 status-error-full">{error}</div>}
          <div className="flex justify-between">
            <button type="button" onClick={() => setStep(1)} className="typo-text-14 text-faded hover:text-full">
              ← {t('forecastWizard.back')}
            </button>
            <Button tone={ButtonToneProps.PRIMARY_BLUE} title={t('forecastWizard.next')} onClick={() => setStep(3)} />
          </div>
        </div>
      )}

      {/* Step 3 — Confirmation */}
      {step === 3 && (
        <div className="px-24 py-24 flex flex-col gap-16">
          <p className="typo-text-14 text-faded">{t('forecastWizard.summaryHint')}</p>

          <div className="grid grid-cols-2 gap-px border border-subtle rounded-base overflow-hidden">
            <div className="surface-elevated p-16">
              <div className="typo-text-10 text-faded font-semibold uppercase tracking-wider">
                {t('forecastWizard.period')}
              </div>
              <div className="typo-text-16--bold text-full">{monthLabel}</div>
            </div>
            <div className="surface-elevated p-16">
              <div className="typo-text-10 text-faded font-semibold uppercase tracking-wider">
                {t('forecastWizard.totalPackets')}
              </div>
              <div className="typo-text-16--bold status-active-full">{localeNumber(total)}</div>
            </div>
            <div className="surface-elevated p-16">
              <div className="typo-text-10 text-faded font-semibold uppercase tracking-wider">
                {t('forecastWizard.workingDaysLabel')}
              </div>
              <div className="typo-text-16--bold text-full">{workingDays}</div>
            </div>
            <div className="surface-elevated p-16">
              <div className="typo-text-10 text-faded font-semibold uppercase tracking-wider">
                {t('forecastWizard.packetsPerDayAvg')}
              </div>
              <div className="typo-text-16--bold text-full">{Math.round(avgPerDay)}</div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-12 typo-text-12 text-faded">
            <span className="font-semibold">{t('forecastWizard.weightingLabel')}:</span>
            {WEEKDAY_KEYS.map(k => (
              <span key={k} className="text-full">
                {t(`forecastWizard.weekday.${k}`)} <strong>{formatWeightPercent(weights[k])}</strong>
              </span>
            ))}
          </div>

          <div className="border border-subtle rounded-base overflow-hidden">
            <div className="px-16 py-8 surface-base flex items-center justify-between">
              <span className="typo-text-10 text-faded font-semibold uppercase tracking-wider">
                {t('forecastWizard.dailyDistribution')}
              </span>
              <span className="typo-text-12 text-faded">{t('forecastWizard.workingDays', { count: workingDays })}</span>
            </div>
            <div>
              <table className="w-full">
                <thead className="surface-base">
                  <tr className="border-b border-subtle">
                    <th className="px-16 py-8 text-left typo-text-10 text-faded font-semibold uppercase tracking-wider">
                      {t('forecastWizard.dateColumn')}
                    </th>
                    <th className="px-16 py-8 text-left typo-text-10 text-faded font-semibold uppercase tracking-wider">
                      {t('forecastWizard.dayColumn')}
                    </th>
                    <th className="px-16 py-8 text-right typo-text-10 text-faded font-semibold uppercase tracking-wider">
                      {t('forecastWizard.targetColumn')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {distribution
                    .filter(row => row.isWorkday)
                    .map(row => (
                      <tr key={row.date.toISOString()} className="border-b border-subtle">
                        <td className="px-16 py-6 typo-text-12 text-full">
                          {row.date.toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit' })}
                        </td>
                        <td className="px-16 py-6 typo-text-12 text-faded">
                          {row.date.toLocaleDateString(currentLocale(), { weekday: 'short' })}
                        </td>
                        <td className="px-16 py-6 text-right typo-text-12--bold">{row.shipped}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          {error && <div className="typo-text-12 status-error-full">{error}</div>}

          <div className="flex justify-between">
            <button type="button" onClick={() => setStep(2)} className="typo-text-14 text-faded hover:text-full">
              ← {t('forecastWizard.back')}
            </button>
            <div className="flex items-center gap-8">
              <Button
                tone={ButtonToneProps.SECONDARY}
                title={saveWizard.isPending ? t('forecasts.saving') : t('forecasts.save_draft')}
                onClick={() => onSaveDraft()}
                disabled={saveWizard.isPending || submit.isPending}
              />
              <Button
                tone={ButtonToneProps.PRIMARY_BLUE}
                title={submit.isPending ? t('forecasts.submitting') : t('forecastWizard.submitForecast')}
                onClick={() => onSubmit()}
                disabled={saveWizard.isPending || submit.isPending || total <= 0}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
